import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ContentCard from './ContentCard';
import EmptyState from './EmptyState';
import { MetricCard } from './MetricCards';
import LoadErrorBox from './ui/LoadErrorBox';
import ConfirmDialog from './ui/ConfirmDialog';
import { useExport } from './header/exporters';
import { useDeleteConfirm } from '../hooks/useDeleteConfirm';
import { useEventsBar } from '../hooks/useEventsBar';
import { useHeaderSearch } from '../hooks/useHeaderSearch';
import { useMoney } from '../hooks/useMoney';
import { useRole } from '../hooks/useRole';
import { useStoredEvents } from '../hooks/useStoredEvents';
import { resetEventsBar, setEventsBar } from '../hooks/useEventsBar';
import {
  deleteEventPermanently, fetchArchivedEvents, refreshEventsFromApi, restoreEvent, type StoredEvent,
} from '../services/EventsService';
import { canDeleteForever } from '../utils/deleteGuard';
import { eventSortKey, isUpcoming } from '../utils/eventDates';
import { formatDate } from '../utils/formatDate';
import { entityPath } from '../utils/entityRoutes';
import { useRecordRefs } from '../utils/recordRefs';
import { recordLabel } from '../utils/recordId';
import { errorMessageOf } from '../utils/apiErrors';
import { useNotifications } from './NotificationProvider';
import '../styles/components/EventsScreens.css';
import { usePublishRecordList } from '../hooks/useRecordList';

const statusType = (status: string): 'active' | 'inactive' | 'pending' | 'completed' => {
  const s = status.toLowerCase();
  if (s === 'completed') return 'completed';
  if (s === 'in progress') return 'active';
  if (s === 'upcoming') return 'pending';
  return 'inactive';
};

export const dateLine = (e: StoredEvent): string => {
  const day = e.date ? (formatDate(e.date, '') || e.date) : '';
  return [day, e.time].filter(Boolean).join(' · ');
};

const EventsList: React.FC = () => {
  // Re-render when the record numbers (PO-130 instead of PO-90F0D6B4) load.
  useRecordRefs();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();
  const { canEdit, canViewFinancials } = useRole();
  const money = useMoney();
  const bar = useEventsBar();
  const search = useHeaderSearch().trim().toLowerCase();
  const cached = useStoredEvents();
  const [archivedList, setArchivedList] = useState<StoredEvent[]>([]);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      if (bar.archived) setArchivedList(await fetchArchivedEvents());
      else await refreshEventsFromApi({ strict: true });
    } catch (err) {
      setLoadError(err);
    } finally {
      setLoading(false);
    }
  }, [bar.archived]);

  useEffect(() => { void load(); }, [load]);

  // Archived switch and filters are per visit: leaving the list puts them back.
  useEffect(() => () => resetEventsBar(), []);

  // A filter whose value no longer exists (the last event of that category was archived) would strand an empty list.
  useEffect(() => {
    if (loading || bar.archived) return;
    if (bar.category && !cached.some(e => e.category === bar.category)) setEventsBar({ category: '' });
    if (bar.status && !cached.some(e => e.status === bar.status)) setEventsBar({ status: '' });
  }, [cached, loading, bar.archived, bar.category, bar.status]);

  const source = bar.archived ? archivedList : cached;
  const visible = useMemo(() => {
    return [...source]
      .filter(e => !bar.category || e.category === bar.category)
      .filter(e => !bar.status || e.status === bar.status)
      .filter(e => !search || [e.title, e.category, e.location, e.venue, e.city].some(v => v?.toLowerCase().includes(search)))
      .sort((a, b) => a.order - b.order || eventSortKey(a.date).localeCompare(eventSortKey(b.date)));
  }, [source, bar.category, bar.status, search]);

  usePublishRecordList('event', visible.map(e => e.id));

  useExport('events', {
    rows: visible,
    ctx: { id: id => recordLabel('event', id), date: d => (d ? formatDate(d, '') || d : '') },
  });

  const del = useDeleteConfirm<StoredEvent>({
    record: 'event',
    perform: event => deleteEventPermanently(event.id),
    onDone: event => { setArchivedList(list => list.filter(e => e.id !== event.id)); showSuccess(`${event.title} deleted`); },
  });

  const handleRestore = async (event: StoredEvent) => {
    try {
      await restoreEvent(event.id);
      setArchivedList(list => list.filter(e => e.id !== event.id));
      showSuccess(`${event.title} restored`);
    } catch (err) {
      showError(errorMessageOf(err, `Could not restore ${event.title}.`));
    }
  };

  const totals = useMemo(() => ({
    count: cached.length,
    upcoming: cached.filter(e => isUpcoming(e.date)).length,
    planned: cached.reduce((sum, e) => sum + (e.headcount ?? 0), 0),
    confirmed: cached.reduce((sum, e) => sum + (e.headcountConfirmed ?? 0), 0),
  }), [cached]);

  return (
    <div className="wt-container fade-in">
      {!bar.archived && (
        <section className="stats-grid" aria-label="Events summary">
          <MetricCard label="Events" value={totals.count} icon="calendar" tone="pink" />
          <MetricCard label="Upcoming" value={totals.upcoming} icon="clock" tone="blue" />
          <MetricCard label="Planned headcount" value={totals.planned} icon="users" tone="teal" />
          <MetricCard label="Confirmed headcount" value={totals.confirmed} icon="check" tone="green" />
        </section>
      )}

      {loadError ? (
        <LoadErrorBox what="events" error={loadError} onRetry={() => void load()} retrying={loading} />
      ) : visible.length === 0 ? (
        loading ? null : bar.archived ? (
          <EmptyState icon="calendar" title="No archived events." description="Events you archive appear here." />
        ) : (
          <EmptyState
            icon="calendar"
            title={source.length === 0 ? 'No events yet.' : 'No events match these filters.'}
            description={source.length === 0 && canEdit ? 'Use New Event to add the first one.' : undefined}
          />
        )
      ) : (
        <div className="events-gallery-grid">
          {visible.map(event => {
            const price = canViewFinancials && event.price !== '' ? money.format(Number(event.price)) : null;
            return (
              <ContentCard
                key={event.id}
                image={event.image || undefined}
                hideImage={!event.image}
                title={event.title}
                subtitle={event.location || event.venue}
                description={event.description}
                status={event.category ? { label: event.category, type: statusType(event.status) } : undefined}
                details={[
                  { label: 'ID', value: recordLabel('event', event.id) },
                  { label: 'Date & Time', value: dateLine(event) },
                  { label: 'Status', value: event.status },
                  ...(price ? [{ label: 'Price', value: price }] : []),
                ].filter(d => d.value)}
                customContent={
                  <>
                    <div className="ev-meta-grid">
                      <div className="ev-meta-item">
                        <span className="ev-meta-label">Headcount</span>
                        <span className="ev-meta-value">
                          {event.headcountConfirmed ?? 0} confirmed{event.headcount !== undefined ? ` / ${event.headcount} planned` : ''}
                        </span>
                      </div>
                      {event.dresscode && (
                        <div className="ev-meta-item">
                          <span className="ev-meta-label">Dress code</span>
                          <span className="ev-meta-value">{event.dresscode}</span>
                        </div>
                      )}
                    </div>
                    {bar.archived && canEdit && (
                      <div className="ev-archived-actions">
                        <button type="button" className="header-action-btn" onClick={() => void handleRestore(event)}>Restore</button>
                        {canDeleteForever(event) && (
                          <button type="button" className="header-action-btn header-btn-danger" onClick={() => del.request(event)}>
                            Delete forever
                          </button>
                        )}
                      </div>
                    )}
                  </>
                }
                onClick={() => {
                  const path = entityPath('event', event.id);
                  if (path) navigate(path);
                }}
              />
            );
          })}
        </div>
      )}

      <ConfirmDialog
        {...del.dialogProps}
        title="Delete event forever"
        subject={del.target?.title}
        consequences={['The event is removed for good and cannot be restored.']}
        confirmLabel="Delete forever"
      />
    </div>
  );
};

export default EventsList;
